/** Unique cart key per held appointment (same service, different slots). */
export function serviceCartLineId(bookingRef) {
  return `svc:${bookingRef}`;
}

export function isServiceAppointmentLine(line) {
  if (!line) return false;
  if (line.productData?.productType !== "Service") return false;
  return Boolean(line.bookingDetails?.startsAt || line.bookingDetails?.bookingRef);
}

/** Cart line key before checkout hold (selection only) or after hold (booking ref). */
export function resolveServiceCartLineId(hold, service) {
  if (hold?.bookingRef) return serviceCartLineId(hold.bookingRef);
  const slotKey = appointmentSlotKeyFromHold(hold, service);
  if (!slotKey) return null;
  return `svc:sel:${slotKey.replace(/\|/g, "--")}`;
}

/** Same service + staff + start time — used to prevent duplicate cart lines. */
export function appointmentSlotKeyFromParts({ productId, staffId, startsAt }) {
  if (!productId || !startsAt) return null;
  const staff = staffId ? String(staffId) : "";
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return null;
  return `${String(productId)}|${staff}|${start.toISOString()}`;
}

export function appointmentSlotKeyFromHold(hold, service) {
  return appointmentSlotKeyFromParts({
    productId: service?._id,
    staffId: hold?.staffId,
    startsAt: hold?.startsAt,
  });
}

export function appointmentSlotKeyFromLine(line) {
  const bd = line?.bookingDetails;
  return appointmentSlotKeyFromParts({
    productId: line?.productData?._id,
    staffId: bd?.staffId,
    startsAt: bd?.startsAt,
  });
}

export function appointmentTimesOverlap(a, b) {
  if (!a?.startsAt || !b?.startsAt) return false;
  const startA = new Date(a.startsAt);
  const startB = new Date(b.startsAt);
  const endA = a.endsAt ? new Date(a.endsAt) : new Date(startA.getTime() + 60 * 60000);
  const endB = b.endsAt ? new Date(b.endsAt) : new Date(startB.getTime() + 60 * 60000);
  if (
    Number.isNaN(startA.getTime()) ||
    Number.isNaN(startB.getTime()) ||
    Number.isNaN(endA.getTime()) ||
    Number.isNaN(endB.getTime())
  ) {
    return false;
  }
  const staffA = a.staffId ? String(a.staffId) : "";
  const staffB = b.staffId ? String(b.staffId) : "";
  if (staffA !== staffB) return false;
  return startA < endB && endA > startB;
}

/** Active (non-expired) duplicate slot already in cart? */
export function findActiveDuplicateAppointmentInCart(cart, slotKey, excludeLineId) {
  if (!slotKey || !cart || typeof cart !== "object") return null;
  for (const [lineId, line] of Object.entries(cart)) {
    if (excludeLineId && lineId === excludeLineId) continue;
    if (!isServiceAppointmentLine(line)) continue;
    if (isAppointmentHoldExpired(line)) continue;
    if (appointmentSlotKeyFromLine(line) === slotKey) return { lineId, line };
  }
  return null;
}

export function buildServiceCartLineFromHold(hold, service) {
  const basePrice = parseFloat(
    hold.priceSnapshot?.baseAmount ??
      service?.finalPrice ??
      service?.price ??
      0
  );
  const addOnTotal = parseFloat(hold.priceSnapshot?.addOnsAmount ?? 0) || 0;
  const unitPrice =
    parseFloat(hold.priceSnapshot?.amount) ||
    (basePrice + addOnTotal) ||
    parseFloat(service?.finalPrice || service?.price || 0);

  return {
    orderQuantiy: 1,
    totalAmount: unitPrice,
    basePrice: unitPrice,
    productData: service,
    shippingRate: 0,
    shippingService: { carrier_name: "Service Appointment" },
    bookingDetails: {
      ...(hold.bookingRef ? { bookingRef: hold.bookingRef } : {}),
      ...(hold.holdExpiresAt ? { holdExpiresAt: hold.holdExpiresAt } : {}),
      startsAt: hold.startsAt,
      endsAt: hold.endsAt,
      timezone: hold.display?.timezone || hold.timezone,
      displayDate: hold.display?.date,
      displayTime: hold.display?.time,
      staffId: hold.staffId || null,
      staffName: hold.staffName || hold.display?.staffName || null,
      serviceFulfillment: hold.serviceFulfillment || null,
      addOns: Array.isArray(hold.selectedAddOns) ? hold.selectedAddOns : [],
      baseServiceAmount: basePrice,
      addOnsAmount: addOnTotal,
    },
  };
}

/** Resolve localStorage cart object key for a grouped cart row. */
export function getAppointmentHoldExpiry(line) {
  const raw = line?.bookingDetails?.holdExpiresAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isAppointmentHoldExpired(line) {
  const exp = getAppointmentHoldExpiry(line);
  if (!exp) return false;
  return exp.getTime() < Date.now();
}

/** Minutes until hold expires; null if unknown or already expired. */
export function minutesUntilAppointmentHoldExpires(line) {
  const exp = getAppointmentHoldExpiry(line);
  if (!exp) return null;
  const ms = exp.getTime() - Date.now();
  if (ms <= 0) return null;
  return Math.ceil(ms / 60000);
}

export function resolveCartLineKey(groupCartItem) {
  if (groupCartItem?.cartLineId) return groupCartItem.cartLineId;
  let cartItem = groupCartItem?.productData?._id;
  if (
    cartItem &&
    groupCartItem?.productData?.productType === "Customizable" &&
    groupCartItem?.selectedVariations?.length
  ) {
    const selectedVariant = groupCartItem.selectedVariations
      .map((v) => v.attributeValue.replace(/\s+/g, ""))
      .join("_");
    if (selectedVariant) cartItem = `${cartItem}_${selectedVariant}`;
  }
  return cartItem;
}
